import { toast } from 'sonner';
import { Order } from '../types';
import { updateOrderStatus, updateOrder, deleteOrder, toggleOrderPaid } from '../services/orders';
import { reportError } from '../lib/error-reporting';

export function useOrderManager() {
    function handleStatusChange(orderId: string, newStatus: 'ready' | 'done') {
        // FIRE AND FORGET - optimistic update
        updateOrderStatus(orderId, newStatus).catch(e => {
            reportError(e, { context: 'useOrderManager.handleStatusChange', orderId, newStatus });
            toast.error('Erreur sync: ' + (e instanceof Error ? e.message : 'Erreur inconnue'));
        });

        if (newStatus === 'ready') {
            toast.success('Commande marquée comme prête');
        } else {
            toast.success('Commande marquée comme récupérée');
        }
    }

    function handleTogglePaid(orderId: string, currentPaidStatus: boolean) {
        // FIRE AND FORGET - optimistic update
        toggleOrderPaid(orderId, !currentPaidStatus).catch(e => {
            reportError(e, { context: 'useOrderManager.handleTogglePaid', orderId });
            toast.error('Erreur sync: ' + (e instanceof Error ? e.message : 'Erreur inconnue'));
        });

        if (!currentPaidStatus) {
            toast.success('Commande marquée comme payée');
        } else {
            toast.info('Commande marquée comme non payée');
        }
    }

    function handleDeleteOrder(orderId: string) {
        if (!confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
            return;
        }
        // FIRE AND FORGET - optimistic update
        deleteOrder(orderId).catch(e => {
            reportError(e, { context: 'useOrderManager.handleDeleteOrder', orderId });
            toast.error('Erreur sync: ' + (e instanceof Error ? e.message : 'Erreur inconnue'));
        });
        toast.success('Commande supprimée');
    }

    function handleMarkAllPreparedAndReady(order: Order) {
        const updatedLines = order.lines.map(l => ({
            ...l,
            preparedQuantity: l.quantity
        }));

        const updatedOrder = {
            ...order,
            lines: updatedLines,
            status: 'ready' as const
        };

        // FIRE AND FORGET - optimistic update
        updateOrder(updatedOrder).catch(e => {
            reportError(e, { context: 'useOrderManager.handleMarkAllPreparedAndReady', orderId: order.id });
            toast.error('Erreur sync: ' + (e instanceof Error ? e.message : 'Erreur inconnue'));
        });
        toast.success('Commande et produits marqués comme prêts');
    }

    function updateLinePreparedByIndex(order: Order, lineIndex: number, isPrepared: boolean) {
        const updatedLines = order.lines.map((l, idx) => {
            if (idx === lineIndex) {
                return {
                    ...l,
                    preparedQuantity: isPrepared ? l.quantity : 0
                };
            }
            return l;
        });

        const allPrepared = updatedLines.every(l => l.preparedQuantity >= l.quantity);
        const somePrepared = updatedLines.some(l => l.preparedQuantity < l.quantity);

        const updatedOrder = {
            ...order,
            lines: updatedLines,
            status: allPrepared
                ? 'ready' as const
                : (order.status === 'ready' ? 'pending' as const : order.status)
        };

        // OPTIMISTIC UPDATE: Do not await server confirmation
        updateOrder(updatedOrder).catch(e => {
            reportError(e, { context: 'useOrderManager.updateLinePreparedByIndex', orderId: order.id, lineIndex });
            toast.error('Erreur sync: ' + (e instanceof Error ? e.message : 'Erreur inconnue'));
        });

        // Immediate feedback based on local logic
        if (allPrepared && order.status === 'pending') {
            toast.success('Tous les produits prêts !');
        } else if (somePrepared && order.status === 'ready') {
            toast.info('Commande repassée en "À préparer"');
        }
    }

    // Toggle preparation status for a specific line by index
    function toggleLinePrepared(order: Order, lineIndex: number) {
        const line = order.lines[lineIndex];
        if (!line) return;
        const isCurrentlyPrepared = line.preparedQuantity >= line.quantity;
        updateLinePreparedByIndex(order, lineIndex, !isCurrentlyPrepared);
    }

    return {
        handleStatusChange,
        handleTogglePaid,
        handleDeleteOrder,
        handleMarkAllPreparedAndReady,
        toggleLinePrepared
    };
}
